import {
  Box,
  MenuItem,
  Pagination,
  PaginationItem,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";
import { z } from "zod";

import { useAccounts } from "@/api/zero/accounts";
import {
  AudienceTableContainer,
  HeaderCell,
  RowCell,
} from "@/features/Audiences/AudienceTable";
import { alternatingRowBg } from "@/features/Audiences/tableUtils";
import { SettingsSection } from "@/features/Settings/components/SettingsSection";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import { ACCOUNT_STATUSES } from "@/features/ZeroAdmin/accounts/service";
import { AccountStatusChip } from "@/features/ZeroAdmin/components/StatusChip";
import { formatStatusLabel } from "@/features/ZeroAdmin/components/statusLabel";
import {
  TableMessageRow,
  TableSkeletonRows,
} from "@/features/ZeroAdmin/components/TableStateRows";
import {
  FirstPageIcon,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  LastPageIcon,
  SearchIcon,
} from "@/icons";

const sortFieldSchema = z.enum(["name", "status"]);

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  sort: sortFieldSchema.optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  page: z.number().int().positive().optional(),
});

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/accounts/",
)({
  validateSearch: zodValidator(searchSchema),
  component: AccountsListPage,
});

const TABLE_COLUMN_COUNT = 3;
const PAGE_SIZE = 50;

type AccountSortField = z.infer<typeof sortFieldSchema>;

function AccountsListPage() {
  const search = Route.useSearch();
  const { siteId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data: accounts, isLoading, isError } = useAccounts();

  const filtered = useMemo(() => {
    const q = (search.q ?? "").trim().toLowerCase();
    const matches = (accounts ?? []).filter((a) => {
      const matchesQ = q === "" || a.name.toLowerCase().includes(q);
      const matchesStatus =
        search.status === undefined || a.status === search.status;
      return matchesQ && matchesStatus;
    });

    if (search.sort === undefined) return matches;

    const direction = search.dir === "desc" ? -1 : 1;
    return [...matches].sort((a, b) => {
      const left = search.sort === "status" ? a.status : a.name;
      const right = search.sort === "status" ? b.status : b.name;
      return left.localeCompare(right) * direction;
    });
  }, [accounts, search.q, search.status, search.sort, search.dir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(Math.max(search.page ?? 1, 1), totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const paged = useMemo(
    () => filtered.slice(pageStart, pageStart + PAGE_SIZE),
    [filtered, pageStart],
  );

  const toggleSort = (field: AccountSortField) =>
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        sort: field,
        dir: prev.sort === field && prev.dir === "asc" ? "desc" : "asc",
        page: undefined,
      }),
    });

  const sortDirection = (field: AccountSortField) =>
    search.sort === field ? (search.dir ?? "asc") : "asc";

  const totalCount = accounts?.length ?? 0;
  const noun = totalCount === 1 ? "account" : "accounts";
  const resultLabel = isLoading
    ? "Loading accounts…"
    : filtered.length === totalCount
      ? `${totalCount} ${noun}`
      : `${filtered.length} of ${totalCount} ${noun}`;

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Admin" }]}
      title="Accounts"
      description="All billing accounts. Click a row to edit."
      data-testid="accounts-list-page"
    >
      <SettingsSection
        title="All accounts"
        description={resultLabel}
        data-testid="accounts-list-section"
        actions={
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name"
            value={search.q ?? ""}
            onChange={(e) =>
              void navigate({
                to: ".",
                search: (prev) => ({
                  ...prev,
                  q: e.target.value || undefined,
                  page: undefined,
                }),
              })
            }
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 2 }} fontSize="small" />,
              },
              htmlInput: { "data-testid": "accounts-search-input" },
            }}
            sx={{ width: 320 }}
          />
        }
      >
        <Stack sx={{ gap: 3 }}>
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              select
              label="Status"
              value={search.status ?? ""}
              onChange={(e) =>
                void navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    status: e.target.value || undefined,
                    page: undefined,
                  }),
                })
              }
              data-testid="accounts-status-filter"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {ACCOUNT_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {formatStatusLabel(status)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <AudienceTableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <HeaderCell size="small">
                    <TableSortLabel
                      active={search.sort === "name"}
                      direction={sortDirection("name")}
                      onClick={() => toggleSort("name")}
                      data-testid="accounts-sort-name"
                    >
                      Name
                    </TableSortLabel>
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 180 }}>
                    <TableSortLabel
                      active={search.sort === "status"}
                      direction={sortDirection("status")}
                      onClick={() => toggleSort("status")}
                      data-testid="accounts-sort-status"
                    >
                      Status
                    </TableSortLabel>
                  </HeaderCell>
                  <HeaderCell size="small" sx={{ width: 120 }}>
                    ID
                  </HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableSkeletonRows columns={TABLE_COLUMN_COUNT} />
                ) : isError ? (
                  <TableMessageRow
                    colSpan={TABLE_COLUMN_COUNT}
                    tone="error"
                    data-testid="accounts-list-error"
                  >
                    Failed to load accounts.
                  </TableMessageRow>
                ) : filtered.length === 0 ? (
                  <TableMessageRow
                    colSpan={TABLE_COLUMN_COUNT}
                    data-testid="accounts-list-empty"
                  >
                    No accounts match these filters.
                  </TableMessageRow>
                ) : (
                  paged.map((account, index) => (
                    <TableRow
                      key={account.id}
                      data-testid={`accounts-row-${account.id}`}
                      hover
                      sx={{
                        cursor: "pointer",
                        backgroundColor: alternatingRowBg(index),
                      }}
                      onClick={() =>
                        void navigate({
                          to: "/settings/$siteId/accounts/$accountId",
                          params: {
                            siteId: siteId ?? "",
                            accountId: account.id,
                          },
                        })
                      }
                    >
                      <RowCell size="small">{account.name}</RowCell>
                      <RowCell size="small">
                        <AccountStatusChip status={account.status} />
                      </RowCell>
                      <RowCell size="small">{account.id}</RowCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </AudienceTableContainer>

          {filtered.length > PAGE_SIZE && (
            <Stack
              direction="row"
              sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
              data-testid="accounts-pagination"
            >
              <Typography variant="body2" color="text.secondary">
                Showing {pageStart + 1}–{pageStart + paged.length} of{" "}
                {filtered.length}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Pagination
                page={page}
                count={totalPages}
                showFirstButton
                showLastButton
                onChange={(_event, nextPage) =>
                  void navigate({
                    to: ".",
                    search: (prev) => ({
                      ...prev,
                      page: nextPage === 1 ? undefined : nextPage,
                    }),
                    replace: true,
                  })
                }
                renderItem={(item) => (
                  <PaginationItem
                    slots={{
                      first: FirstPageIcon,
                      last: LastPageIcon,
                      previous: KeyboardArrowLeftIcon,
                      next: KeyboardArrowRightIcon,
                    }}
                    {...item}
                  />
                )}
              />
            </Stack>
          )}
        </Stack>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
